export const BABY_NAMES_HEADLINES = {
  en: "Baby Names by Nakshatra",
  hi: "नक्षत्र के अनुसार बच्चे के नाम",
} as const;
/**
 * The engine-derived direct answer for /baby-names/[nakshatra]/ (AEO).
 *
 * Same contract as nakshatra-answer.ts: every fact — the four pada starting
 * syllables, ruler, deity, gana — already exists in the governed nakshatra
 * domain data (packages/domain-data, codegen from backend YAML), so the answer
 * is COMPOSED from the engine's data, never authored and never LLM-written
 * (rules 24/29). One function covers all 27 members.
 *
 * The 40-60 word bound is sourced in page_quality_contract.yaml
 * (`answer_summary`); `baby-names-answer.test.ts` proves EVERY nakshatra lands
 * inside it, which is what stops a longer deity name or a two-word nakshatra
 * from silently pushing one member out of contract.
 */

import { nakshatras, type Nakshatra } from "@ishvaram/domain-data";

/**
 * The hub answer for /baby-names/. Same governed source as the per-nakshatra
 * answer below: the roster length and every nakshatra's own pada-letter list
 * come from the codegen'd domain data (baby_names.yaml), so the counts cannot
 * drift from the data the calculator and the letters table render.
 */
export function babyNamesHubAnswer(): string {
  const nakshatraCount = nakshatras.length;
  const padaCount = nakshatras.reduce((acc, n) => acc + n.letters.length, 0);
  return (
    `Vedic naamkaran chooses a baby's name from the Janma Nakshatra — the ` +
    `lunar mansion the Moon occupied at the exact birth time. Each of the ` +
    `${nakshatraCount} nakshatras divides into four padas, and every pada ` +
    `prescribes its own starting syllable, so the birth moment fixes one of ` +
    `${padaCount} traditional name-starting sounds.`
  );
}

/**
 * The Hindi twin of the hub answer. ONE governed roster yields both locales, so
 * /baby-names/ and /hi/baby-names/ can never state different counts (rules
 * 23/24) — the timing-answer.ts EN/HI pattern.
 */
export function babyNamesHubAnswerHi(): string {
  const nakshatraCount = nakshatras.length;
  const padaCount = nakshatras.reduce((acc, n) => acc + n.letters.length, 0);
  return (
    `वैदिक नामकरण में बच्चे का नाम जन्म नक्षत्र से चुना जाता है — वह नक्षत्र जिसमें जन्म के ` +
    `ठीक समय चंद्रमा स्थित होता है। ${nakshatraCount} नक्षत्रों में से हर एक चार पदों में ` +
    `बँटा है और हर पद का अपना प्रारंभिक अक्षर होता है, इसलिए जन्म का क्षण ${padaCount} ` +
    `पारंपरिक नाम-अक्षरों में से एक तय करता है।`
  );
}

export function babyNamesAnswer(n: Nakshatra): string {
  const letters = n.letters.join(", ");
  return (
    `Baby names for ${n.name_en} (${n.name_hi}) nakshatra traditionally begin ` +
    `with the syllables ${letters} — one for each of its four padas. The pada ` +
    `of the Moon at the exact birth time selects the starting letter. ` +
    `${n.name_en} is ruled by ${n.ruler} with deity ${n.deity}, and carries ${n.gana} gana.`
  );
}
